"use client";

import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { useIsDark } from "@/context/ThemeContext";
import { twMerge } from "tailwind-merge";
import { reviews } from "@/constants/Data";
import Image from "next/image";

export default function ReviewSlider() {
  const isdark = useIsDark();
  const swiperRef = useRef(null);
  const [slidesPerView, setSlidesPerView] = useState(2);
  const [slidesPerGroup, setSlidesPerGroup] = useState(2);
  const [spaceBetween, setSpaceBetween] = useState(20);

  // Import Swiper CSS dynamically on the client-side
  useEffect(() => {
    import("swiper/css");
    import("swiper/css/autoplay");
  }, []);

  useEffect(() => {
    const updateSwiperSettings = () => {
      const width = window.innerWidth;

      if (width < 900) {
        // Below 900px - 1 review
        setSlidesPerView(1);
        setSlidesPerGroup(1);
        setSpaceBetween(15);
      } else {
        setSlidesPerView(2);
        setSlidesPerGroup(1);
        setSpaceBetween(24);
      }
    };

    updateSwiperSettings();
    window.addEventListener("resize", updateSwiperSettings);
    return () => window.removeEventListener("resize", updateSwiperSettings);
  }, []);

  const [page, setPage] = useState(0);

  return (
    <div className="relative w-full flex items-center justify-center">
      <div className="w-full max-w-[1400px]">
        <Swiper
          slidesPerView={slidesPerView}
          slidesPerGroup={slidesPerGroup}
          spaceBetween={spaceBetween}
          className="w-full"
          speed={1200}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
          }}
          navigation={false}
          loop={true}
          modules={[Autoplay]}
          onBeforeInit={(swiper) => {
            swiperRef.current = swiper;
          }}
          onSlideChange={(swiper) => {
            const currentIndex = swiper.realIndex;
            setPage(currentIndex % 6);
          }}
          breakpoints={{
            0: {
              slidesPerView: 1,
              slidesPerGroup: 1,
              spaceBetween: 10,
            },
            900: {
              slidesPerView: 2,
              slidesPerGroup: 1,
              spaceBetween: 24,
            },
          }}
        >
          {reviews.map((review, index) => (
            <SwiperSlide key={index} className="!flex !justify-center">
              <ReviewCard
                name={review.name}
                image={review.image}
                date={review.date}
                email={review.email}
                title={review.title}
                stars={review.stars}
                review={review.review}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* 6 circle section */}
        <div className="flex items-center justify-center mt-6 sm:mt-8">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <button
              key={i}
              aria-label={`Go to page ${i + 1}`}
              onClick={() => {
                swiperRef.current?.slideToLoop(i);
              }}
              className={twMerge(
                "h-2 w-2 sm:h-3 sm:w-3 rounded-full mx-1 transition-all duration-300 border",
                isdark ? "border-[#4C4C4C]" : "border-[#5A5A6EDB]",
                i === page ? (isdark ? "bg-[#4C4C4C]" : "bg-[#1A1A1A]") : ""
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ReviewCard({ name, image, date, email, title, stars, review }) {
  const isdark = useIsDark();

  return (
    <div
      className={`w-full max-w-[610px] mx-auto h-full flex items-center justify-center rounded-2xl ${isdark ? "" : "reviewcard-wrapper"}`}
    >
      <div className="reviewcard-container w-full h-auto flex flex-col gap-5 md:gap-4 lg:gap-3 pb-3">
        {/* Header with circles and date */}
        <div
          className="flex font-inter justify-between w-full py-2 px-3 sm:py-4 sm:px-5 lg:py-4 lg:px-5 rounded-t-2xl flex-shrink-0"
          style={{
            borderBottom: `1px solid ${isdark ? "#FFFFFF0D" : "#0000000D"}`,
          }}
        >
          <div className="flex items-center gap-1 sm:gap-2">
            <div
              className="rounded-full w-2 h-2 sm:w-3 sm:h-3 lg:w-[10px] lg:h-[10px]"
              style={{
                border: `1px solid ${isdark ? "#FFFFFF4D" : "#1A1A1AB0"}`,
              }}
            />
            <div
              className="rounded-full w-2 h-2 sm:w-3 sm:h-3 lg:w-[10px] lg:h-[10px]"
              style={{
                border: `1px solid ${isdark ? "#FFFFFF4D" : "#1A1A1AB0"}`,
              }}
            />
            <div
              className="rounded-full w-2 h-2 sm:w-3 sm:h-3 lg:w-[10px] lg:h-[10px]"
              style={{
                border: `1px solid ${isdark ? "#FFFFFF4D" : "#1A1A1AB0"}`,
              }}
            />
          </div>

          <div
            className={`flex items-center gap-1 text-xs sm:text-sm ${
              isdark ? "text-[#FFFFFF80]" : "text-[#00000080]"
            }`}
          >
            <img
              src={isdark ? "/calender.svg" : "/calendarLight.svg"}
              alt="Calendar"
              className="w-3 h-3 sm:w-5 sm:h-5 lg:w-6 lg:h-6"
            />
            <span className="text-[8px] sm:text-xs lg:text-[12px]">{date}</span>
          </div>
        </div>

        {/* Profile section */}
        <div className="px-3 sm:px-5 lg:px-0 lg:mx-10 lg:mt-9 lg:mb-[26px] shrink-0">
          {/* Desktop layout */}
          <div className="hidden sm:flex justify-between items-start">
            <div
              className={`flex items-center h-[52] w-fit gradient-profile-border ${
                isdark
                  ? "gradient-profile-border-dark"
                  : "gradient-profile-border-light"
              }`}
            >
              <div className="profile-inner gap-2 sm:gap-3">
                <div
                  className={`rounded-full p-0.5 gradient-profile-border ${
                    isdark
                      ? "gradient-profile-border-dark"
                      : "gradient-profile-border-light"
                  }`}
                >
                  <Image
                    src={image}
                    alt={name}
                    width={56}
                    height={56}
                    sizes="(max-width: 640px) 24px, (max-width: 1024px) 28px, 28px"
                    className="w-6 h-6 sm:w-7 sm:h-7 lg:w-7 lg:h-7 rounded-full object-cover"
                    data-nosnippet
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex font-normal font-inter not-italic text-xs sm:text-sm lg:text-sm laptop:text-[14px]">
                    <p className="dark:text-[#FCFDFFEF] text-[#1A1A1A] truncate">
                      {name}
                    </p>
                    {/* <p className="dark:text-[#F1F7FEB5] text-[#000000B5] ml-1 truncate">
                      &lt;{email}&gt;
                    </p> */}
                  </div>
                  <p className="text-[10px] laptop:text-[10px] 3xl:text-[12px] truncate dark:text-[#F1F7FEB5] text-[#000000B5]">
                    {title}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex -mt-2 text-[#FF1CF7] text-sm sm:text-lg lg:text-2xl ml-2">
              {"★★★★★".slice(0, stars)}
            </div>
          </div>

          {/* Mobile layout  */}
          <div className="sm:hidden">
            <div
              className={`flex items-center w-fit gradient-profile-border mb-2 ${
                isdark
                  ? "gradient-profile-border-dark"
                  : "gradient-profile-border-light"
              }`}
            >
              <div className="profile-inner gap-2">
                <div
                  className={`rounded-full p-px gradient-profile-border ${
                    isdark
                      ? "gradient-profile-border-dark"
                      : "gradient-profile-border-light"
                  }`}
                >
                  <Image
                    src={image}
                    alt={name}
                    width={40}
                    height={40}
                    sizes="20px"
                    className="w-5 h-5 rounded-full object-cover"
                    data-nosnippet
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex font-normal font-inter not-italic text-[10px]">
                    <p className="dark:text-[#FCFDFFEF] text-[#1A1A1A] truncate">
                      {name}
                    </p>
                    <p className="dark:text-[#F1F7FEB5] text-[#000000B5] ml-1 truncate">
                      &lt;{email}&gt;
                    </p>
                  </div>
                  <p className="text-[8px] truncate dark:text-[#F1F7FEB5] text-[#000000B5]">
                    {title}
                  </p>
                </div>
              </div>
            </div>

            {/* Stars on mobile */}
            <div className="flex text-[#FF1CF7] text-xs">
              {"★★★★★".slice(0, stars)}
            </div>
          </div>
        </div>

        {/* Review text   */}
        <div className="h-full opacity-75">
          <p className="font-inter font-normal not-italic text-xs h-full sm:text-base lg:text-[16px] leading-relaxed px-3 sm:px-6 lg:px-10 line-clamp-4 sm:line-clamp-4 grow dark:text-[#F1F7FEB5] text-[#000000B5]">
            {review}
          </p>
        </div>
      </div>
    </div>
  );
}
